import { baseApi } from './baseApi';

// ── Tipos (espejo de la Business API de Phylax) ──────────────────────────────
export interface CrmEscalation {
  _id: string;
  phone: string;
  customerName: string;
  question: string;
  context?: string;
  /** Código de propiedad de Imperium (IMP-…) si la consulta refiere a una. */
  propertyId?: string;
  priority: 'normal' | 'urgent';
  status: 'pending' | 'in_progress' | 'accepted' | 'resolved' | 'rejected' | 'expired';
  /** Título de la propiedad, venga de la consulta o de la que se asoció al derivarla. */
  propertyTitle?: string;
  createdAt: string;
  acceptedAt?: string | null;
  /** Asesor al que un admin le derivó la consulta a mano (ver escalationVisibility). */
  /**
   * Asesor a cargo. Sale del dueño de la propiedad (`via: 'propiedad'`) o de una
   * derivación manual (`via: 'derivacion'`, que gana sobre la anterior).
   */
  asesor?: {
    userId: string; name: string; avatar?: string; officeName?: string;
    via: 'propiedad' | 'derivacion'; derivadaPor?: string;
    /**
     * ¿Puede atender? Lo resuelve el backend con el criterio único
     * (`atencionDelAsesor.ts`). Sin esto, un asesor dado de baja se veía en la fila
     * igual que cualquier otro y la consulta se quedaba ahí sin que nadie lo notara.
     */
    atiende?: boolean;
    /** Qué decir en la pastilla: "Asesor inactivo" · "Sin acceso aún". */
    motivo?: string;
  } | null;
  derivadaA?: { userId: string; name: string; at?: string };
  /** Quién la derivó, para que el asesor sepa de dónde le llegó. */
  derivadaPor?: string;
}

/** Propiedad linkeada a una consulta (card mini del hilo). */
export interface CrmEscalationProperty {
  propertyId: string;
  title?: string;
  type?: string;
  operation?: string;
  status?: string;
  media?: { images?: Array<{ url: string }> };
  pricing?: { salePrice?: number; rentPrice?: number; currency?: string };
  location?: { neighborhood?: string; city?: string };
}

export interface CrmEscalationThread {
  ok: boolean;
  escalation: CrmEscalation;
  property: CrmEscalationProperty | null;
  customer: Record<string, unknown> | null;
  messages: CrmMessage[];
}

export interface NumeroListaNegra {
  number: string;
  alias: string;
  tipo: 'cliente' | 'agente' | 'agente_inactivo';
  /** Nombre y usuario del agente, si es uno. */
  nombre?: string;
  userId?: string;
  /** Teléfono hasheado: así identifica el monitor de Gustavo las charlas de Phylax. */
  hash?: string;
}

export interface CrmConversation {
  phone: string;
  lastMessage: string;
  lastRole: 'user' | 'assistant' | 'system';
  lastAt: string;
  lastType: string;
  totalMessages: number;
  customer?: {
    _id?: string; firstName?: string; lastName?: string; status?: string;
    leadScore?: string; botMode?: string;
    /** Último mensaje del cliente — con esto se sabe si la ventana de 24 h sigue abierta. */
    lastInboundAt?: string;
  } | null;
  openEscalation?: { _id?: string; status: string; question: string; priority: string; createdAt: string } | null;
  /** En "Atención manual": el Agente Virtual no le contesta nunca a este número. */
  atencionManual?: boolean;
}

/** Charla de un visitante con Gustavo en el chat de la web (no pasa por Phylax). */
export interface WebConversation {
  sessionId: string;
  /** Código corto para nombrarla ("W-3F9A2C"): se le escribe por ahí desde soporte. */
  codigo: string;
  visitor: { firstName?: string; phone?: string; email?: string } | null;
  customerId: string | null;
  lastMessage?: string;
  lastRole?: 'user' | 'assistant';
  lastSender?: 'agent';
  lastAt?: string;
  totalMessages: number;
  /** Tomada por una persona: Gustavo no contesta mientras esté. */
  manual: { porNombre?: string; desde?: string } | null;
  /** El chat del visitante está abierto ahora: lo que le escribas lo ve en el momento. */
  online: boolean;
  location: { city?: string; country?: string } | null;
}

export interface CrmMessage {
  role: 'user' | 'assistant' | 'system';
  content: string;
  messageType: string;
  channel?: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
}

export interface CrmEvent {
  id: string;
  title?: string;
  date?: string;
  time?: string;
  status?: string;
  outcome?: string;
  location?: string;
  /**
   * `customerId` lo resuelve el CRM cruzando el teléfono: Phylax manda nombre y número
   * pero no conoce nuestros ids. Con él, el nombre en la agenda abre la ficha.
   */
  customer?: { name?: string; phone?: string; customerId?: string } | null;
  /** `operation` la agrega el CRM al listar: define las palabras del cierre. */
  property?: { code?: string; title?: string; operation?: string } | null;
  /**
   * Asesor a cargo, resuelto por Imperium desde el dueño de la propiedad.
   *
   * Phylax devuelve `agent: null` en todas —allá "agente" es otra cosa—, así que este
   * bloque lo arma el backend del CRM (ver /phylax/events).
   */
  agent?: {
    userId?: string; name?: string; avatar?: string; officeName?: string;
    /** false también cuando NO hay asesor: entonces `name` viene vacío y `motivo` dice
     *  "Sin asesor". Es lo que permite el filtro "Sin asesor" de la pantalla. */
    atiende?: boolean;
    motivo?: string;
  } | null;
  createdAt?: string;
  /** Ediciones del evento espejado en Imperium: qué cambió, quién y por qué. */
  /** Por qué se cayó (features/visitas/MotivoCancelacion). Viene del espejo del CRM. */
  cancellationCategory?: string;
  cancellationReason?: string;
  editHistory?: Array<{
    at: string; byName?: string; via?: string; reason?: string;
    changes: Array<{ field: string; from?: string; to?: string }>;
  }>;
}

export interface CrmCustomer {
  id: string;
  name: string;
  phone: string;
  email?: string;
  status?: string;
  lead_score?: string;
  acquisition_source?: string;
  created_at?: string;
  last_message_at?: string;
  profile?: Record<string, unknown> | null;
}

export interface CrmAnalytics {
  ok: boolean;
  /** Ventana REAL usada, ya recortada a la puesta en marcha del Agente. */
  days?: number;
  /** Cuándo arrancó el Agente en esta inmobiliaria (ISO). Antes de eso no se cuenta nada. */
  goLive?: string;
  /** true si la ventana pedida era más larga que el tiempo que lleva andando. */
  recortadoPorArranque?: boolean;
  /** Campos viejos — se mantienen por compatibilidad, la pantalla usa los bloques. */
  total_leads?: number;
  hot_leads?: number;
  new_leads_30d?: number;
  total_properties?: number;
  active_properties?: number;
  visits_total?: number;
  visits_completed?: number;
  sales?: number;
  escalations_open?: number;

  period?: { days: number; from: string; to: string; prev_from: string; prev_to: string };
  plan?: {
    messages_period: number; messages_max: number;
    tts_minutes_used: number; tts_minutes_max: number;
    usage_period: string | null; billing_day: number | null;
  };
  activity?: {
    messages_in: number; messages_out: number; messages_in_prev: number;
    after_hours_pct: number; after_hours_count: number;
    bot_first_reply_median_sec: number | null;
    business_hours: { start: number; end: number; days: number[] };
  };
  escalations?: {
    pending: number; older_7d: number; accepted: number; resolved: number;
    median_accept_min: number | null;
  };
  visits?: {
    scheduled: number; scheduled_total: number; pending_outcome: number;
    completed: number;
    /** Visitas que CERRARON, sin importar la operación. */
    closed?: number;
    /** El desglose de `closed`. Puede sumar MENOS que el total: las propiedades con
     *  operación `both` o sin dato no caen en ningún cajón. */
    sales: number; rentals?: number; reservations?: number;
  };
  leads?: { total: number; new_period: number; prev_period: number; with_score: number; hot: number };
}

/** Lo que dejaron las conversaciones (extracción nocturna de Phylax). */
type CitaInsight = { quote: string; detail?: string; phone?: string };
/** El cliente del CRM con ese teléfono, si tiene ficha (lo agrega el backend). */
type ClienteCrm = { id: string; nombre: string };

export interface CrmInsights {
  ok: boolean;
  enabled: boolean;
  days?: number;
  goLive?: string;
  recortadoPorArranque?: boolean;
  summary: {
    days: number;
    totalConversations: number;
    objections: Array<{ category: string; productCode?: string; count: number; quotes?: CitaInsight[] }>;
    complaints: Array<{ category: string; count: number; quotes?: CitaInsight[] }>;
    likes: Array<{ topic: string; productCode?: string; count: number; quotes?: CitaInsight[] }>;
    kbGaps: Array<{ topic: string; count: number; questions: string[] }>;
    outcomes: Array<{
      outcome: string; count: number;
      /** Una frase por charla: por qué terminó así. Las 10 más recientes. */
      reasons?: Array<{ reason: string; phone?: string; cliente?: ClienteCrm }>;
    }>;
    competitors?: Array<{ name: string; count: number; contexts?: string[] }>;
    hotSignals: Array<{ phone: string; outcome: string; lastMessageAt: string; signals?: string[]; cliente?: ClienteCrm }>;
    sentimentWorsened: number;
    sentimentImproved: number;
  } | null;
  /** El resumen semanal con acciones sugeridas (lo arma Phylax los lunes). */
  digest?: {
    _id: string;
    weekOf: string;
    conversationsAnalyzed?: number;
    actions: Array<{
      title: string; detail?: string; impact?: 'high' | 'med' | 'low';
      type?: string; evidenceCount?: number; status?: 'new' | 'done' | 'dismissed';
    }>;
  } | null;
}

/** Cuántas visitas se caen y por qué, medido en el CRM (GET /crm/phylax/cancelaciones). */
export interface CancelacionesVisitas {
  ok: boolean;
  desde: string | null;
  total: number;
  canceladas: number;
  erroresDeCarga: number;
  porOrigen: Array<{ origen: 'phylax' | 'ui' | 'gustavo'; total: number; canceladas: number }>;
  motivos: Array<{ categoria: string; label: string; count: number; ejemplos: string[] }>;
  sinMotivo: number;
  ultimas: Array<{
    fecha: string; cliente: string; propiedad?: string; origen: 'phylax' | 'ui' | 'gustavo';
    categoria?: string; motivo?: string;
  }>;
}

/** Si los asesores contestan las consultas que deriva el Agente (GET /crm/phylax/respuesta-asesores). */
export interface FilaRespuestaAsesor {
  userId: string; nombre: string; recibidas: number;
  contestadas: number; cerradasSinRespuesta: number; cerradasPorOtro: number;
  abiertas: number; atrasadas: number; medianaHoras: number | null; tarde: number;
  /** Cerradas sin registro de quién (las resolvió el Agente o se cerraron por fuera). */
  sinDato: number;
}
export interface RespuestaAsesores {
  ok: boolean; desde: string | null; total: number;
  contestadas: number; cerradasSinRespuesta: number; cerradasPorOtro: number;
  abiertas: number; atrasadas: number; medianaHoras: number | null; sinAsesor: number; sinDato: number;
  /** Se pidió desde antes del 28/09, cuando empezó el registro de quién cierra: se recortó. */
  recortadoPorRegistro?: boolean;
  asesores: FilaRespuestaAsesor[];
}

/** Mensajes del plan de Phylax en el ciclo (GET /crm/phylax/uso). incluidos -1 = ilimitado. */
export interface UsoPhylax {
  periodo: string; cuenta: 'salientes' | 'mensajes_de_clientes';
  incluidos: number; usados: number; extra: number; desdeElCrm: number;
  excedente: { activo: boolean; precioUsd: number; estimadoUsd: number; cobro: 'tarjeta' | 'factura' | 'saldo' | null; proximoCorte?: string };
  saldoUsd?: number; mensajesConSaldo?: number;
}

/** Saldo prepago de mensajes extra en Phylax y sus movimientos (GET /crm/phylax/saldo). */
export interface PlantillasPhylax {
  ok: boolean;
  desde: string;
  precioUsd: number;
  /** El interruptor de la org en Phylax (`avisosPorPlantilla`). */
  activo: boolean;
  plantillas: Array<{ plantilla: string; cantidad: number; usd: number; ultima: string }>;
}

export interface SaldoPhylax {
  ok: boolean; saldoUsd: number; mensajesQueAlcanzan: number; precioUsd: number;
  sinSaldo: 'factura' | 'pausa';
  recarga: { minUsd: number; maxUsd: number };
  movimientos: Array<{
    tipo: 'carga_tarjeta' | 'carga_admin' | 'descuento_admin' | 'puesto_en_cero' | 'consumo' | 'plantilla' | 'plantilla_anulada';
    usd: number; saldoDespuesUsd: number; mensajes?: number; dia?: string; fecha: string;
    por?: string; nota?: string;
  }>;
}

/** WhatsApp de Gustavo a los asesores en el mes (GET /crm/phylax/gustavo-whatsapp). */
export interface GustavoWhatsapp {
  periodo: string; salientes: number; respuestas: number; avisos: number;
  fotos: number; audios: number; asesores: number; costoUsd: number;
}

export interface CrmCredits {
  ok: boolean;
  balance?: number;
  reserved?: number;
  packs?: Array<{ pack?: string; units?: number; purchasedAt?: string; expiresAt?: string }>;
}

/** Qué propiedades de cada asesor atiende el Agente Virtual, y por qué las otras no. */
/** Por qué el Agente Virtual no la ofrece. `null` = sí la ofrece. */
export type MotivoFueraDelBot = 'asesor' | 'ficha' | null;

export interface PropiedadDeAgente {
  /** `_id` de Mongo. Es lo que pide el endpoint de conexión, no el código. */
  id:        string;
  codigo:    string;
  titulo?:   string;
  operacion: string | null;
  /**
   * `conectada`    → el aviso de ML lo atiende el Agente Virtual
   * `puede`        → está publicada en ML y todavía no se prendió ← lo accionable
   * `aviso_cerrado` → tiene meliId pero el aviso está cerrado en ML: republicar antes
   * `sin_publicar` → sin aviso en ML no hay teléfono que cambiar
   */
  conexion: 'conectada' | 'puede' | 'aviso_cerrado' | 'sin_publicar';
  /**
   * Por qué el Agente Virtual NO la ofrece. Es OTRA COSA que `conexion`: conectada habla
   * del teléfono del aviso de ML; esto, de si el bot la conoce.
   *   'asesor' → la regla permanente del asesor. No se libera de a una.
   *   'ficha'  → la lista negra, esta en particular.
   *   null     → el bot la ofrece.
   */
  fueraDelBot?: MotivoFueraDelBot;
}
export interface AgenteConPropiedades {
  agentId:     string;
  nombre:      string;
  /** Para agrupar la lista. 'Sin oficina' si el asesor no tiene una asignada. */
  oficina:     string;
  /** Sus propiedades ACTIVAS. */
  total:       number;
  conectadas:  number;
  /** Publicadas en ML y sin conectar: lo que puede ganar hoy. */
  puede:       number;
  /**
   * El asesor pidió que el Agente Virtual NO ofrezca sus propiedades.
   *
   * ⚠️ No confundir con "desconectada": desconectar es el teléfono del aviso de ML. Esto
   * es que el bot no las conoce — no salen en su cartelera ni como "similares". En la web
   * de Imperium y en los portales siguen publicadas igual.
   */
  ocultoDelBot?: boolean;
  /** El asesor confirma él las visitas: el bot ofrece sus fichas pero no cierra horario. */
  confirmaVisitas?: boolean;
  propiedades: PropiedadDeAgente[];
}
export interface PropiedadesPorAgente {
  /** La oficina de quien mira, cuando es admin de una. El front la abre desplegada. */
  miOficina?: string | null;
  /**
   * El backend ya recortó a las propiedades de quien mira (asesor). No es lo mismo que
   * "soy agente": lo dice el servidor, que es quien hizo el recorte. Deducirlo del rol
   * acá sería copiar una regla que vive allá.
   */
  soloMias?: boolean;
  agentes: AgenteConPropiedades[];
  /**
   * Activas sin asesor asignado (dueño inactivo o nunca asignadas), con su oficina. No
   * cuelgan de ningún asesor, así que sin esto la lista detrás de un contador quedaba
   * corta respecto del total. (15/09/2026)
   */
  sinAsesor?: Array<PropiedadDeAgente & { oficina?: string }>;
  totales: {
    /** Todas las activas del tenant. */
    propiedades: number;
    conectadas:  number;
    /** Publicadas en ML y sin conectar. */
    puede:       number;
    /** Sin aviso en ML: no se pueden conectar todavía. */
    sinPublicar: number;
    /** Con meliId pero el aviso CERRADO en MercadoLibre: no se conecta, se republica. */
    avisoCerrado?: number;
    /** Activas cuyo dueño ya no es un usuario activo. Cartera viva sin nadie atrás. */
    sinAgente:   number;
    agentes:     number;
    /** Fuera de la cartelera del Agente por la regla permanente del asesor. */
    fueraDelBotPorAsesor: number;
    /** Fuera por la lista negra, ficha por ficha. */
    fueraDelBotPorFicha:  number;
  };
}


/**
 * Embudo del Agente Virtual medido EN EL CRM.
 *
 * Distinto del embudo que ya existía, que sale entero de la base de Phylax y cuenta sus
 * leads y sus visitas según el `outcome` que se marcó de aquel lado. Este arranca de los
 * clientes que Phylax creó en el CRM y sigue su rastro hasta la reserva: es el único que
 * puede responder si el Agente generó negocio. (19/08/2026)
 */
export interface EmbudoNegocio {
  ok: boolean;
  desde: string | null;
  clientes: { hablaron: number; total: number; enVentana: number; yaEranClientes: number };
  eventos: {
    agendados: number; conCita: number;
    actividades: number; visitas: number; conActividad: number;
    /** Personas distintas con al menos una visita (lo que va al lado de las visitas). */
    conVisita?: number;
    visitasSinResultado: number;
    /** Opcional: un backend sin el deploy de 12/10/2026 no lo manda. */
    visitasPorEstado?: { pendientes: number; confirmadas: number; canceladas: number };
  };
  consultas: { asignadas: number; conPropiedad: number };
  reservadas: Array<{ propertyId: string; titulo: string; operacion: string; cliente: string }>;
  cerradas: Array<{ propertyId: string; titulo: string; estado: string; cliente: string }>;
  /** Reservas que se cayeron. Solo se cuentan desde el 25/09/2026 (antes no se guardaba de quién eran). */
  caidas?: Array<{ propertyId: string; titulo: string; cliente: string }>;
}

export const crmPhylaxApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    getCrmPhylaxStatus: b.query<{ ok: boolean; enabled: boolean; branchId: string | null }, void>({
      query: () => '/crm/phylax/status',
    }),

    // Consultas (escalaciones)
    getPropiedadesPorAgente: b.query<PropiedadesPorAgente & { ok: boolean }, { officeId?: string } | void>({
      query: (p) => ({ url: '/crm/phylax/propiedades-por-agente', params: p ?? undefined }),
      /**
       * Sin esto no se enteraba de nada: conectabas un aviso, la lista seguía mostrando
       * el conteo viejo y había que recargar la página para verlo. La mutación que
       * conecta ya invalida `Property/LIST` —desde acá y desde la ficha—, pero esta
       * query no proveía ningún tag, así que la invalidación no la alcanzaba.
       * (19/08/2026, pedido del user)
       */
      providesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    /**
     * Prende o apaga "que el Agente Virtual no ofrezca mis propiedades".
     *
     * Devuelve `coListing`: cuántas de las que se esconden las trabaja con otro asesor.
     * Ese compañero no eligió nada, y en la cartelera 595 de 995 están en co-listing — la
     * pantalla lo dice antes de que el asesor se entere por un reclamo.
     */
    setOcultoDelBot: b.mutation<
      { ok: boolean; agentId: string; ocultoDelBot: boolean; fichas: number; coListing: number },
      { agentId: string; ocultar: boolean }
    >({
      query: ({ agentId, ocultar }) => ({
        url: `/crm/phylax/asesores/${agentId}/oculto-del-bot`,
        method: 'PATCH',
        body: { ocultar },
      }),
      invalidatesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    /**
     * "Ofrecé mis propiedades, pero el horario lo confirmo yo."
     *
     * ⛔ NO es `setOcultoDelBot`, que es lo contrario: ahí el bot deja de ofrecerlas.
     */
    setConfirmaVisitas: b.mutation<
      { ok: boolean; agentId: string; confirmaVisitas: boolean; fichas: number },
      { agentId: string; confirmar: boolean }
    >({
      query: ({ agentId, confirmar }) => ({
        url: `/crm/phylax/asesores/${agentId}/confirma-visitas`,
        method: 'PATCH',
        body: { confirmar },
      }),
      invalidatesTags: [{ type: 'Property', id: 'LIST' }],
    }),

    listCrmEscalations: b.query<{ ok: boolean; total: number; escalations: CrmEscalation[] }, { status?: string } | void>({
      query: (arg) => `/crm/phylax/escalations${arg && arg.status ? `?status=${encodeURIComponent(arg.status)}` : ''}`,
      providesTags: ['PhylaxEscalation'],
    }),
    acceptCrmEscalation: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/crm/phylax/escalations/${id}/accept`, method: 'POST' }),
      invalidatesTags: ['PhylaxEscalation', 'PhylaxConversation'],
    }),
    /**
     * "Tomar control" desde una CONSULTA: el asesor solo sobre la charla de su consulta. Con
     * el control tomado el Agente se calla hasta que se lo devuelva; cerrar o contestar la
     * consulta lo conservan. (04/11/2026)
     */
    setEscalationBotMode: b.mutation<{ ok: boolean; botMode: string }, { id: string; phone: string; mode: 'human' | 'auto' }>({
      query: ({ id, mode }) => ({ url: `/crm/phylax/escalations/${id}/bot-mode`, method: 'PATCH', body: { mode } }),
      invalidatesTags: (_r, _e, { id, phone }) => [{ type: 'PhylaxConvHistory' as const, id: `esc-${id}` }, { type: 'PhylaxConvHistory' as const, id: phone }, 'PhylaxConversation'],
    }),
    /**
     * Tomar o soltar una charla SIN consulta abierta de por medio.
     * 'human' = atiende una persona y el Agente se calla; 'auto' = vuelve el Agente.
     */
    setCrmBotMode: b.mutation<{ ok: boolean; botMode: string }, { customerId: string; phone: string; mode: 'human' | 'auto' }>({
      query: ({ customerId, mode }) => ({
        url: `/crm/phylax/conversations/${customerId}/bot-mode`, method: 'PATCH', body: { mode },
      }),
      invalidatesTags: (_r, _e, { phone }) => [{ type: 'PhylaxConvHistory' as const, id: phone }, 'PhylaxConversation'],
    }),
    /**
     * "Atención manual": el Agente Virtual deja de contestarle a ese número (los mensajes se
     * siguen guardando). Para cortar a quien le escribe para molestar. Solo admin y Soporte.
     */
    /** La lista negra, con quién es cada número: cliente o agente (activo o dado de baja). */
    listarAtencionManual: b.query<{ ok: boolean; numeros: NumeroListaNegra[] }, void>({
      query: () => '/crm/phylax/atencion-manual',
      providesTags: ['PhylaxConversation'],
    }),
    buscarAgentesListaNegra: b.query<{ ok: boolean; agentes: Array<{ id: string; nombre: string; phone: string; activo: boolean }> }, string>({
      query: (q) => `/crm/phylax/atencion-manual/agentes?q=${encodeURIComponent(q)}`,
    }),
    agregarAtencionManual: b.mutation<{ ok: boolean; yaEstaba?: boolean }, { phone?: string; nombre?: string; userId?: string; clienteHash?: string }>({
      query: (body) => ({ url: '/crm/phylax/atencion-manual', method: 'POST', body }),
      invalidatesTags: ['PhylaxConversation'],
    }),
    quitarAtencionManual: b.mutation<{ ok: boolean }, { phone: string }>({
      query: ({ phone }) => ({ url: `/crm/phylax/atencion-manual/${encodeURIComponent(phone)}`, method: 'DELETE' }),
      invalidatesTags: ['PhylaxConversation'],
    }),
    /**
     * Derivar la consulta a un asesor, con una propiedad suya opcional.
     *
     * `resolver: true` la cierra en el mismo movimiento: el asesor recibe igual el
     * WhatsApp y la consulta sale de pendientes. Es para cuando el asesor YA está
     * hablando con el cliente y el pendiente quedó colgado. El backend responde
     * `resuelta` — puede haber derivado y no haber podido cerrar.
     */
    assignCrmEscalation: b.mutation<
      { ok: boolean; assignedTo: { userId: string; name: string }; propertyId?: string; resuelta?: boolean },
      { id: string; userId: string; propertyId?: string; resolver?: boolean }
    >({
      query: ({ id, userId, propertyId, resolver }) => ({
        url: `/crm/phylax/escalations/${id}/assign`, method: 'POST', body: { userId, propertyId, resolver },
      }),
      invalidatesTags: ['PhylaxEscalation'],
    }),
    /**
     * `respuesta` = lo que contestó el asesor a "¿ya hablaste con el cliente?": `avisar` hace
     * que el Agente Virtual le escriba al cliente antes de cerrar. Ver `CerrarConsultaDialog`.
     */
    resolveCrmEscalation: b.mutation<{ ok: boolean }, { id: string; resolution_note?: string; respuesta?: 'ya_hablo' | 'avisar' }>({
      query: ({ id, resolution_note, respuesta }) => ({ url: `/crm/phylax/escalations/${id}/resolve`, method: 'POST', body: { resolution_note, respuesta } }),
      invalidatesTags: ['PhylaxEscalation', 'PhylaxConversation'],
    }),
    rejectCrmEscalation: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/crm/phylax/escalations/${id}/reject`, method: 'POST' }),
      invalidatesTags: ['PhylaxEscalation', 'PhylaxConversation'],
    }),
    // Hilo de la consulta: escalación + propiedad + historial (único camino del agente
    // al historial — scoped a su consulta).
    getCrmEscalationThread: b.query<CrmEscalationThread, string>({
      query: (id) => `/crm/phylax/escalations/${id}/thread`,
      providesTags: (_r, _e, id) => [{ type: 'PhylaxConvHistory' as const, id: `esc-${id}` }],
    }),
    // Responder la consulta: sale por el bot. Si estaba pendiente, la toma primero.
    replyCrmEscalation: b.mutation<{ ok: boolean; message_id?: string }, { id: string; message: string }>({
      query: ({ id, message }) => ({ url: `/crm/phylax/escalations/${id}/reply`, method: 'POST', body: { message } }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'PhylaxConvHistory' as const, id: `esc-${id}` }, 'PhylaxEscalation', 'PhylaxConversation',
      ],
    }),

    // Conversaciones
    listCrmConversations: b.query<{ ok: boolean; total: number; conversations: CrmConversation[]; canalEnVivo?: string }, void>({
      query: () => '/crm/phylax/conversations',
      providesTags: ['PhylaxConversation'],
      // Un inbox que no se actualiza solo no es un inbox: con la pestaña abierta desde
      // la tarde, la lista quedaba congelada en esa hora y las charlas nuevas no
      // aparecían nunca. Se refresca cada 30 s y al volver a la pestaña.
      keepUnusedDataFor: 60,
    }),
    getCrmHistory: b.query<{ ok: boolean; phone: string; customer: Record<string, unknown> | null; messages: CrmMessage[]; has_more: boolean }, string>({
      query: (phone) => `/crm/phylax/conversations/${encodeURIComponent(phone)}/history`,
      providesTags: (_r, _e, phone) => [{ type: 'PhylaxConvHistory' as const, id: phone }],
    }),
    /**
     * La charla del Agente Virtual colgada de UNA visita agendada.
     *
     * Va por el evento y no por el teléfono a propósito: el endpoint del teléfono es
     * solo para admins, y esto lo abre al asesor que tiene la visita. La visita es lo
     * que da derecho a leer, así que es también la clave de la consulta.
     */
    getEventHistory: b.query<
      { ok: boolean; phone: string; customer: Record<string, unknown> | null; messages: CrmMessage[]; has_more: boolean },
      string
    >({
      query: (eventId) => `/crm/phylax/events/${eventId}/history`,
      providesTags: (_r, _e, eventId) => [{ type: 'PhylaxConvHistory' as const, id: `ev-${eventId}` }],
    }),
    sendCrmMessage: b.mutation<{ ok: boolean; message_id: string }, { customerId: string; phone: string; message: string }>({
      query: ({ customerId, message }) => ({ url: `/crm/phylax/conversations/${customerId}/send`, method: 'POST', body: { message } }),
      invalidatesTags: (_r, _e, { phone }) => [{ type: 'PhylaxConvHistory' as const, id: phone }, 'PhylaxConversation'],
    }),

    // Charlas del chat de la web: mismo inbox, datos propios del CRM.
    listWebConversations: b.query<{ ok: boolean; total: number; conversations: WebConversation[] }, void>({
      query: () => '/crm/web/conversations',
      providesTags: ['PhylaxConversation'],
      keepUnusedDataFor: 60,
    }),
    getWebHistory: b.query<{ ok: boolean; conversation: WebConversation; messages: CrmMessage[] }, string>({
      query: (sessionId) => `/crm/web/conversations/${encodeURIComponent(sessionId)}/history`,
      providesTags: (_r, _e, sessionId) => [{ type: 'PhylaxConvHistory' as const, id: `web-${sessionId}` }],
    }),
    setWebBotMode: b.mutation<{ ok: boolean }, { sessionId: string; mode: 'human' | 'auto' }>({
      query: ({ sessionId, mode }) => ({
        url: `/crm/web/conversations/${encodeURIComponent(sessionId)}/bot-mode`, method: 'PATCH', body: { mode },
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [{ type: 'PhylaxConvHistory' as const, id: `web-${sessionId}` }, 'PhylaxConversation'],
    }),
    sendWebMessage: b.mutation<{ ok: boolean }, { sessionId: string; message: string }>({
      query: ({ sessionId, message }) => ({
        url: `/crm/web/conversations/${encodeURIComponent(sessionId)}/send`, method: 'POST', body: { message },
      }),
      invalidatesTags: (_r, _e, { sessionId }) => [{ type: 'PhylaxConvHistory' as const, id: `web-${sessionId}` }, 'PhylaxConversation'],
    }),

    // Eventos / visitas
    listCrmEvents: b.query<
      { ok: boolean; total: number; events: CrmEvent[] },
      { status?: string; sinAsesor?: boolean } | void
    >({
      query: (arg) => {
        const qs = new URLSearchParams();
        if (arg && arg.status) qs.set('status', arg.status);
        if (arg && arg.sinAsesor) qs.set('sinAsesor', 'true');
        const q = qs.toString();
        return `/crm/phylax/events${q ? `?${q}` : ''}`;
      },
      providesTags: ['PhylaxEvent'],
    }),

    /**
     * Ponerle asesor a una visita que no tiene (o que lo tiene dado de baja).
     *
     * Es la reparación de las visitas huérfanas: hasta ahora el único camino era cerrarlas
     * desde ¿Qué pasó?, un formulario que no permite asignar ni asesor ni propiedad.
     */
    asignarAsesorAVisita: b.mutation<
      { ok: boolean; asesor: { userId: string; name: string } },
      { id: string; userId: string; propertyId?: string }
    >({
      query: ({ id, userId, propertyId }) => ({
        url: `/crm/phylax/events/${id}/asesor`, method: 'POST', body: { userId, propertyId },
      }),
      invalidatesTags: ['PhylaxEvent'],
    }),
    // `reason` es obligatorio del lado del backend cuando se reprograma (fecha/hora/lugar):
    // queda en el historial del evento junto con quién lo cambió.
    updateCrmEvent: b.mutation<
      { ok: boolean; status: string; outcome: string },
      { id: string; status?: string; outcome?: string; date?: string; time?: string; location?: string; reason?: string;
        /** Lo que pasó, contado por el agente. Va al mismo campo que las Notas de la ficha
         *  de la propiedad, así el rastro es el mismo se cierre desde donde se cierre. */
        nota?: string;
        /** Por qué se cayó, al cancelar (features/visitas/MotivoCancelacion). */
        cancellationCategory?: string; cancellationReason?: string }
    >({
      query: ({ id, ...body }) => ({ url: `/crm/phylax/events/${id}`, method: 'PATCH', body }),
      invalidatesTags: ['PhylaxEvent', 'PhylaxAnalytics'],
    }),

    // Clientes / leads
    listCrmCustomers: b.query<{ ok: boolean; total: number; customers: CrmCustomer[] }, { status?: string; lead_score?: string } | void>({
      query: (arg) => {
        const p = new URLSearchParams();
        if (arg && arg.status) p.set('status', arg.status);
        if (arg && arg.lead_score) p.set('lead_score', arg.lead_score);
        const s = p.toString();
        return `/crm/phylax/customers${s ? `?${s}` : ''}`;
      },
      providesTags: ['PhylaxCustomer'],
    }),
    getCrmCustomer: b.query<{ ok: boolean; customer: Record<string, unknown> }, string>({
      query: (id) => `/crm/phylax/customers/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'PhylaxCustomer' as const, id }],
    }),
    updateCrmCustomer: b.mutation<{ ok: boolean }, { id: string; status?: string; private_description?: string }>({
      query: ({ id, status, private_description }) => ({ url: `/crm/phylax/customers/${id}`, method: 'PATCH', body: { status, private_description } }),
      invalidatesTags: (_r, _e, { id }) => ['PhylaxCustomer', { type: 'PhylaxCustomer' as const, id }],
    }),

    // Resumen / analítica + créditos
    getCrmAnalytics: b.query<CrmAnalytics, { days?: number } | void>({
      query: (arg) => `/crm/phylax/analytics${arg && arg.days ? `?days=${arg.days}` : ''}`,
      providesTags: ['PhylaxAnalytics'],
    }),
    getCrmInsights: b.query<CrmInsights, { days?: number } | void>({
      query: (arg) => `/crm/phylax/insights${arg && arg.days ? `?days=${arg.days}` : ''}`,
      providesTags: ['PhylaxAnalytics'],
    }),
    getRespuestaAsesores: b.query<RespuestaAsesores, { desde?: string } | void>({
      query: (p) => ({ url: '/crm/phylax/respuesta-asesores', params: p?.desde ? { desde: p.desde } : undefined }),
      providesTags: ['PhylaxAnalytics'],
    }),
    getCancelacionesVisitas: b.query<CancelacionesVisitas, { desde?: string } | void>({
      query: (p) => ({ url: '/crm/phylax/cancelaciones', params: p?.desde ? { desde: p.desde } : undefined }),
      providesTags: ['PhylaxEvent'],
    }),
    getEmbudoNegocio: b.query<EmbudoNegocio, { desde?: string } | void>({
      query: (arg) => `/crm/phylax/embudo-negocio${arg && arg.desde ? `?desde=${arg.desde}` : ''}`,
      providesTags: ['PhylaxAnalytics'],
    }),
    getCrmCredits: b.query<CrmCredits, void>({
      query: () => '/crm/phylax/credits',
      providesTags: ['PhylaxAnalytics'],
    }),
    getUsoPhylax: b.query<UsoPhylax, void>({
      query: () => '/crm/phylax/uso',
      providesTags: ['PhylaxAnalytics'],
    }),
    getSaldoPhylax: b.query<SaldoPhylax, void>({
      query: () => '/crm/phylax/saldo',
      providesTags: ['PhylaxAnalytics'],
    }),
    /** Plantillas de WhatsApp del mes, por tipo: cuántas salieron y cuánto se descontó del saldo. */
    getPlantillasPhylax: b.query<PlantillasPhylax, void>({
      query: () => '/crm/phylax/plantillas',
      providesTags: ['PhylaxAnalytics'],
    }),
    /** Link de pago de Phylax (Culqi de Phylax) para cargar saldo; vence a las 2 h. */
    crearLinkRecarga: b.mutation<{ ok: boolean; url: string; venceEnMinutos: number }, void>({
      query: () => ({ url: '/crm/phylax/saldo/recargar-link', method: 'POST', body: {} }),
    }),
    /** WhatsApp que Gustavo les mandó a los asesores en el mes (suman a la cuota de Phylax). */
    getGustavoWhatsapp: b.query<GustavoWhatsapp, void>({
      query: () => '/crm/phylax/gustavo-whatsapp',
      providesTags: ['PhylaxAnalytics'],
    }),
  }),
});

export const {
  useGetCrmPhylaxStatusQuery,
  useGetPropiedadesPorAgenteQuery,
  useSetOcultoDelBotMutation,
  useSetConfirmaVisitasMutation,
  useListCrmEscalationsQuery,
  useAcceptCrmEscalationMutation,
  useSetCrmBotModeMutation, useSetEscalationBotModeMutation,
  useListarAtencionManualQuery,
  useBuscarAgentesListaNegraQuery,
  useAgregarAtencionManualMutation,
  useQuitarAtencionManualMutation,
  useAssignCrmEscalationMutation,
  useResolveCrmEscalationMutation,
  useRejectCrmEscalationMutation,
  useGetCrmEscalationThreadQuery,
  useReplyCrmEscalationMutation,
  useListCrmConversationsQuery,
  useListWebConversationsQuery,
  useGetWebHistoryQuery,
  useSetWebBotModeMutation,
  useSendWebMessageMutation,
  useGetCrmHistoryQuery,
  useGetEventHistoryQuery,
  useSendCrmMessageMutation,
  useListCrmEventsQuery,
  useUpdateCrmEventMutation,
  useAsignarAsesorAVisitaMutation,
  useListCrmCustomersQuery,
  useGetCrmCustomerQuery,
  useUpdateCrmCustomerMutation,
  useGetCrmAnalyticsQuery,
  useGetGustavoWhatsappQuery,
  useGetUsoPhylaxQuery,
  useGetSaldoPhylaxQuery,
  useGetPlantillasPhylaxQuery,
  useCrearLinkRecargaMutation,
  useGetCrmInsightsQuery,
  useGetEmbudoNegocioQuery,
  useGetCancelacionesVisitasQuery,
  useGetRespuestaAsesoresQuery,
  useGetCrmCreditsQuery,
} = crmPhylaxApi;
